"use client";
import React, { useEffect, useMemo, useState } from "react";
import { Layout, Menu, Button, ConfigProvider, App, Badge, Space, Typography, Divider, Dropdown, Avatar, Popover, List, Empty } from "antd";
import type { MenuProps } from "antd";
import {
  DashboardOutlined, ApiOutlined, SafetyOutlined, UnorderedListOutlined,
  AuditOutlined, UserOutlined, KeyOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  BellOutlined, LogoutOutlined, SettingOutlined, LockOutlined,
} from "@ant-design/icons";
import { useRouter, usePathname } from "next/navigation";
import { useWebSocket } from "@/shared/hooks/use-websocket";

const { Sider, Content, Header } = Layout;
const { Text } = Typography;

const SIDEBAR_BG = "#08142b";
const SIDEBAR_HOVER = "#0e2040";
const PRIMARY = "#003087";

interface NavItem {
  key: string;
  icon: React.ReactNode;
  label: string;
}

interface NavGroup {
  type: "group";
  label: React.ReactNode;
  children: NavItem[];
}

const OPERATIONS_ITEMS: NavItem[] = [
  { key: "/dashboard", icon: <DashboardOutlined />, label: "Dashboard" },
  { key: "/broker-management", icon: <ApiOutlined />, label: "Broker Management" },
  { key: "/risk-oversight", icon: <SafetyOutlined />, label: "Risk Oversight" },
  { key: "/order-monitoring", icon: <UnorderedListOutlined />, label: "Order Monitoring" },
  { key: "/compliance", icon: <AuditOutlined />, label: "Compliance" },
  { key: "/settings", icon: <SettingOutlined />, label: "Settings" },
];

const ADMIN_ITEMS: NavItem[] = [
  { key: "/admin/users", icon: <UserOutlined />, label: "Users" },
  { key: "/admin/access", icon: <KeyOutlined />, label: "Access Control" },
];

const ALL_ITEMS = [...OPERATIONS_ITEMS, ...ADMIN_ITEMS];

// Role-based visibility: which nav keys each role can see
const ROLE_NAV_ALLOWLIST: Record<string, string[]> = {
  admin: ALL_ITEMS.map((i) => i.key),
  superadmin: ALL_ITEMS.map((i) => i.key),
  operator: ALL_ITEMS.map((i) => i.key),
  manager: ["/dashboard", "/broker-management", "/risk-oversight", "/order-monitoring", "/settings"],
  compliance: ["/dashboard", "/compliance", "/settings"],
  analyst: ["/dashboard", "/settings"],
  viewer: ["/dashboard"],
  employee: ["/dashboard", "/settings"],
};

function filterNavGroups(userRoles: string[]): NavGroup[] {
  if (userRoles.length === 0) return [];

  const allowed = new Set<string>();
  for (const role of userRoles) {
    const keys = ROLE_NAV_ALLOWLIST[role] ?? ROLE_NAV_ALLOWLIST.employee;
    for (const key of keys) allowed.add(key);
  }

  const ops = OPERATIONS_ITEMS.filter((item) => allowed.has(item.key));
  const admin = ADMIN_ITEMS.filter((item) => allowed.has(item.key));

  const groups: NavGroup[] = [];
  if (ops.length > 0) {
    groups.push({
      type: "group" as const,
      label: <span style={{ fontSize: 10, letterSpacing: "0.1em", color: "rgba(255,255,255,0.3)", textTransform: "uppercase" }}>Operations</span>,
      children: ops,
    });
  }
  if (admin.length > 0) {
    groups.push({
      type: "group" as const,
      label: <span style={{ fontSize: 10, letterSpacing: "0.1em", color: "rgba(255,255,255,0.3)", textTransform: "uppercase" }}>Administration</span>,
      children: admin,
    });
  }
  return groups;
}

type EmployeeUser = {
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  clearance_level?: string;
};

export function EmployeeLayout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState<EmployeeUser | null>(null);
  const { status: wsStatus, alerts: wsAlerts, dismissAlert } = useWebSocket();
  const router = useRouter();
  const pathname = usePathname();

  const pageTitle = ALL_ITEMS.find((i) => i.key === pathname)?.label ?? "Portal";
  const isAuthPage = pathname?.startsWith("/auth/");
  const displayName = useMemo(() => {
    if (!user) return "Operations Portal";
    return [user.first_name ?? user.firstName, user.last_name ?? user.lastName].filter(Boolean).join(" ")
      || user.username || user.email || "Employee";
  }, [user]);
  const initials = useMemo(() => {
    const nameInitials = displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
    return nameInitials || user?.email?.[0]?.toUpperCase() || "OP";
  }, [displayName, user?.email]);
  const displayRole = useMemo(() => {
    const role = user?.roles?.[0] ?? "employee";
    return role.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
  }, [user]);

    const alertsContent = wsAlerts.length === 0 ? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No alerts" style={{ minWidth: 280 }} />
  ) : (
    <List
      size="small"
      style={{ minWidth: 320, maxHeight: 300, overflow: "auto" }}
      dataSource={wsAlerts.slice(0, 10)}
      renderItem={(alert) => (
        <List.Item
          actions={[<Button type="text" size="small" onClick={() => dismissAlert(alert.key)}>×</Button>]}
        >
          <List.Item.Meta
            avatar={<span style={{ width: 8, height: 8, borderRadius: "50%", background: alert.severity === "critical" ? "#dc2626" : alert.severity === "warning" ? "#d97706" : "#2563eb", display: "inline-block", marginTop: 6 }} />}
            title={<Text style={{ fontSize: 12 }}>{alert.message}</Text>}
            description={<Text type="secondary" style={{ fontSize: 10 }}>{alert.time}</Text>}
          />
        </List.Item>
      )}
    />
  );

  const navGroups = useMemo(() => filterNavGroups(user?.roles ?? []), [user?.roles]);
  const navItems: MenuProps["items"] = navGroups.map((group) => ({
    ...group,
    children: group.children.map((item) => ({
      key: item.key,
      icon: item.icon,
      label: item.label,
    })),
  }));

  useEffect(() => {
    if (isAuthPage) return;
    let alive = true;
    fetch("/api/auth/session", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (alive && data?.user) setUser(data.user);
      })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, [isAuthPage]);

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    router.push("/auth/employee");
    router.refresh();
  };

  const profileMenuItems: MenuProps["items"] = [
    {
      key: "profile",
      disabled: true,
      label: (
        <div style={{ minWidth: 220, padding: "4px 0" }}>
          <Text strong style={{ display: "block" }}>{displayName}</Text>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>{user?.email ?? "Signed in employee"}</Text>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>{displayRole}</Text>
        </div>
      ),
    },
    { type: "divider" },
    { key: "/settings", icon: <SettingOutlined />, label: "Account settings" },
    { key: "/admin/access", icon: <LockOutlined />, label: "Access and security" },
    { key: "logout", icon: <LogoutOutlined />, danger: true, label: "Sign out" },
  ];

  if (isAuthPage) {
    return (
      <ConfigProvider theme={{ token: { colorPrimary: PRIMARY, borderRadius: 4, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" } }}>
        <App>{children}</App>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={{ token: { colorPrimary: PRIMARY, colorBgContainer: "#ffffff", colorBgLayout: "#edf0f7", borderRadius: 4, borderRadiusLG: 6, colorText: "#1a2535", colorTextSecondary: "#64748b", colorBorder: "#dde3f0", fontSize: 14, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif", colorSuccess: "#15803d", colorError: "#dc2626", colorWarning: "#d97706", colorInfo: "#2563eb" }, components: { Card: { paddingLG: 20, boxShadowTertiary: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)" }, Table: { headerBg: "#f8fafc", headerColor: "#64748b", headerSortActiveBg: "#f1f5f9", rowHoverBg: "#f5f8ff", fontSize: 13 }, Statistic: { titleFontSize: 12, contentFontSize: 26 }, Menu: { darkItemBg: SIDEBAR_BG, darkSubMenuItemBg: SIDEBAR_BG, darkItemSelectedBg: PRIMARY, darkItemHoverBg: SIDEBAR_HOVER, itemHeight: 40, fontSize: 13, groupTitleColor: "rgba(255,255,255,0.35)" }, Layout: { siderBg: SIDEBAR_BG, headerBg: "#ffffff" }, Button: { borderRadius: 4 }, Tag: { borderRadius: 3, fontSize: 12 }, Input: { borderRadius: 4 } } }}>
      <App>
      <Layout style={{ minHeight: "100vh" }}>
        <Sider trigger={null} collapsible collapsed={collapsed} width={230} style={{ background: SIDEBAR_BG, borderRight: "1px solid rgba(255,255,255,0.05)", position: "sticky", top: 0, height: "100vh", overflow: "auto" }}>
          <div style={{ height: 56, display: "flex", alignItems: "center", padding: "0 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", gap: 10, overflow: "hidden" }}>
            <div style={{ width: 30, height: 30, borderRadius: 4, background: PRIMARY, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 14, flexShrink: 0, letterSpacing: "-0.5px" }}>M</div>
            {!collapsed && (
              <div style={{ lineHeight: 1 }}>
                <div style={{ color: "#ffffff", fontWeight: 700, fontSize: 13, letterSpacing: "0.04em" }}>MECHAGORA</div>
                <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 9, letterSpacing: "0.12em", marginTop: 2 }}>EMPLOYEE PORTAL</div>
              </div>
            )}
          </div>
          <Menu theme="dark" mode="inline" selectedKeys={[pathname]} items={navItems} onClick={({ key }) => router.push(key)} style={{ background: SIDEBAR_BG, border: "none", marginTop: 8, paddingBottom: 24 }} />
          {!collapsed && (
            <div style={{ position: "absolute", bottom: 16, left: 0, right: 0, padding: "0 16px" }}>
              <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 4, padding: "6px 10px", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} />
                <Text style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>sandbox · dev</Text>
              </div>
            </div>
          )}
        </Sider>
        <Layout>
          <Header style={{ padding: "0 24px", background: "#ffffff", borderBottom: "1px solid #e8edf4", height: 56, lineHeight: "56px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 1px 0 #e8edf4" }}>
            <Space size={12} align="center">
              <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed((s) => !s)} style={{ color: "#64748b", height: 36, width: 36 }} />
              <Divider vertical style={{ margin: 0, height: 20, borderColor: "#dde3f0" }} />
              <Text style={{ fontSize: 14, fontWeight: 600, color: "#1a2535" }}>{pageTitle}</Text>
            </Space>
            <Space size={8} align="center">
              <Popover content={alertsContent} title="Notifications" trigger="click" placement="bottomRight">
                <Badge count={wsAlerts.length} size="small" offset={[-2, 2]}>
                  <Button
                    type="text"
                    icon={<BellOutlined />}
                    style={{ color: wsAlerts.length > 0 ? "#dc2626" : wsStatus === "connected" ? "#2563eb" : "#64748b" }}
                    title={wsStatus === "connected" ? "Real-time connected" : wsStatus === "connecting" ? "Connecting..." : "Real-time disconnected"}
                  />
                </Badge>
              </Popover>
              <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items: profileMenuItems, onClick: ({ key }) => { if (key === "logout") { handleLogout(); return; } router.push(String(key)); } }}>
                <Button type="text" style={{ height: 40, padding: "0 6px" }}>
                  <Space size={8}>
                    <Avatar style={{ background: PRIMARY, fontSize: 12, fontWeight: 700 }}>{initials}</Avatar>
                    <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1 }}>
                      <Text style={{ fontSize: 12, fontWeight: 600, maxWidth: 150 }} ellipsis>{displayName}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>{displayRole}</Text>
                    </span>
                  </Space>
                </Button>
              </Dropdown>
            </Space>
          </Header>
          <Content style={{ margin: 24, minHeight: "calc(100vh - 56px - 48px)" }}>{children}</Content>
        </Layout>
      </Layout>
      </App>
    </ConfigProvider>
  );
}
